import React from "react";

import { Settings, Link } from "@mui/icons-material";

import { GuiState, useGuiState } from "@framework/GuiMessageBroker";
import { DashboardTopic } from "@framework/internal/Dashboard";
import type { Workbench } from "@framework/Workbench";
import { usePublishSubscribeTopicValue } from "@lib/utils/PublishSubscribeDelegate";
import { resolveClassNames } from "@lib/utils/resolveClassNames";

import { useActiveDashboard } from "../ActiveDashboardBoundary";

import { EmptySettingsPlaceholder } from "./private-components/emptySettingsPlaceholder";
import { ModuleSettingsHeader } from "./private-components/moduleSettingsHeader";
import { ModuleSettingsStack } from "./private-components/moduleSettingsStack";
import { ModuleSyncSettings } from "./private-components/moduleSyncSettings";

enum DrawerContent {
    ModuleSettings = "ModuleSettings",
    SyncSettings = "SyncSettings",
}

type LeftSettingsPanelProps = {
    workbench: Workbench;
};

export function LeftSettingsPanel(props: LeftSettingsPanelProps): React.ReactNode {
    const [isCollapsed, setIsCollapsed] = useGuiState(
        props.workbench.getGuiMessageBroker(),
        GuiState.LeftSettingsPanelIsCollapsed,
    );
    const [drawerContent, setDrawerContent] = React.useState<DrawerContent>(DrawerContent.ModuleSettings);

    const dashboard = useActiveDashboard();
    const activeModuleInstanceId = usePublishSubscribeTopicValue(dashboard, DashboardTopic.ACTIVE_MODULE_INSTANCE_ID);
    const activeModuleInstance = activeModuleInstanceId ? dashboard.getModuleInstance(activeModuleInstanceId) : null;

    function handleSetDrawerContent(content: string) {
        if (content in DrawerContent) {
            setDrawerContent(content as DrawerContent);
            return;
        }
        setDrawerContent(DrawerContent.ModuleSettings);
    }

    function handleExpandPanel() {
        setIsCollapsed(false);
    }

    function handleCollapsePanel() {
        setIsCollapsed(true);
    }

    return (
        <div className="bg-surface flex h-full flex-col overflow-hidden">
            <ModuleSettingsHeader
                activeTab={drawerContent}
                availableTabs={{
                    [DrawerContent.ModuleSettings]: {
                        title: "Module Settings",
                        icon: <Settings fontSize="small" />,
                    },
                    [DrawerContent.SyncSettings]: {
                        title: "Sync Settings",
                        icon: <Link fontSize="small" />,
                    },
                }}
                activeModuleInstance={activeModuleInstance}
                isCollapsed={isCollapsed}
                onExpandClick={handleExpandPanel}
                onCollapseClick={handleCollapsePanel}
                onTabChange={handleSetDrawerContent}
            />
            <>
                <ModuleSyncSettings
                    workbench={props.workbench}
                    visible={!isCollapsed && drawerContent === DrawerContent.SyncSettings}
                />
                <div
                    className={resolveClassNames(
                        !isCollapsed && drawerContent === DrawerContent.ModuleSettings ? "flex flex-col" : "hidden",
                        "min-h-0 flex-1",
                        "w-full",
                    )}
                >
                    <ModuleSettingsStack workbench={props.workbench} />
                    {!activeModuleInstanceId && <EmptySettingsPlaceholder text="No module selected" />}
                </div>
            </>
        </div>
    );
}
